import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  ApiVisa,
  GlcodeService,
  GlcodeContainerModel,
  Pagination,
  FinancialItemGLCodeLinkService,
  FinancialItemGLCodeLinkContainerModel,
  CompanyGLCodeLinkService,
  CompanyGLCodeLinkContainerModel,
  FinancialItemService,
  FinancialItemContainerModel,
} from "blg-akaun-ts-lib";
import { ReplaySubject, BehaviorSubject , combineLatest, of } from 'rxjs';
import { takeUntil, map, switchMap, startWith, debounceTime, filter, tap, catchError } from 'rxjs/operators';
// import "rxjs-compat/add/operator/map";
import { SubSink } from 'subsink2';

@Component({
  selector: 'app-select-item-gl-code',
  templateUrl: './select-item-gl-code.component.html',
  styleUrls: ['./select-item-gl-code.component.css']
})

export class SelectItemGlCodeComponent implements OnInit, OnDestroy {

  @Input() apiVisa: ApiVisa;
  @Input() guid_glcode = new FormControl();
  @Input() required: boolean;
  @Input() coaGuid$: BehaviorSubject <string>;
  @Input() compGuid$: BehaviorSubject <string>; 
  @Input() editMode: boolean;
  @Input() itemGuid$: BehaviorSubject <string>;
  @Input() txn_code: string;

  serverSideFiltering = new FormControl();
  filteredOptions$: ReplaySubject<GlcodeContainerModel[]> = new ReplaySubject<GlcodeContainerModel[]>(1);
  glCodeList: GlcodeContainerModel[] = [];
  protected subs = new SubSink();

  constructor(
    private glCodeService: GlcodeService,
    private companyGlCodeLinkService: CompanyGLCodeLinkService,
    private financialItemGlCodeLinkService: FinancialItemGLCodeLinkService,
    private financialItemService: FinancialItemService,
  ) {}

  ngOnInit() {
    // Convert it to an Observable
    const editMode$ = of(this.editMode);

    // Observables for conditions
    const firstCondition$ = combineLatest([this.itemGuid$, this.compGuid$]).pipe(
      filter(([itemGuid, compGuid]) => !!compGuid && !!itemGuid), // Proceed only if both compGuid and itemGuid are not null
      switchMap(([itemGuid, compGuid]) => {
        const itemCompPagination = new Pagination();
        itemCompPagination.conditionalCriteria = [
          { columnName: "guid_item", operator: "=", value: itemGuid },
          { columnName: "guid_comp", operator: "=", value: compGuid },
          { columnName: "txn_code", operator: "=", value: this.txn_code },
        ];
        return this.financialItemGlCodeLinkService.getByCriteria(itemCompPagination, this.apiVisa);
      }),
      map(response => response.data?.[0]?.bl_fi_mst_item_company_glcode_link?.guid_glcode),
    );

    const secondCondition$ = this.itemGuid$.pipe(
      filter(itemGuid => !!itemGuid), // Proceed only if itemGuid is not null
      switchMap(itemGuid => this.financialItemService.getByGuid(itemGuid, this.apiVisa)),
      map(response => {
        const details = response.data.bl_fi_mst_item_hdr;
        switch (this.txn_code) {
          case "PURCHASE": return details?.glcode_guid_purchase;
          case "SALES": return details?.glcode_guid_sales_revenue;
          case "SALES_RETURN": return details?.glcode_guid_sales_returns;
          case "PURCHASE": return details?.glcode_guid_purchase;
          case "PURCHASE_RETURN": return details?.glcode_guid_purchase_returns;
          case "PURCHASE_DISCOUNT": return details?.glcode_guid_purchase_discount;
          default: return null;
        }
      }),
    );

    const thirdCondition$ = this.compGuid$.pipe(
        filter(compGuid => !!compGuid), // Only proceed if compGuid is not null
      switchMap(compGuid => {
        const compPagination = new Pagination();
        compPagination.conditionalCriteria = [
          { columnName: "guid_comp", operator: "=", value: compGuid },
          { columnName: "txn_code", operator: "=", value: this.txn_code},
        ];
        return this.companyGlCodeLinkService.getByCriteria(compPagination, this.apiVisa);
      }),
      map(response => response.data?.[0]?.bl_fi_mst_company_glcode_link?.guid_glcode),
    );

    // Reactive stream that combines and handles both edit and create modes
    this.subs.sink = editMode$.pipe(
      switchMap(editMode => {
        // If item edit
        if (editMode) {
          // Existing behavior for edit mode
          return combineLatest([
            this.coaGuid$.pipe(startWith(null)),
            this.guid_glcode.valueChanges.pipe(startWith(this.guid_glcode.value)),
            this.serverSideFiltering.valueChanges.pipe(startWith(''))
          ]).pipe(
            switchMap(([coaGuid, savedGlCodeGuid, searchWord]) =>
              this.getGLCodes(coaGuid, savedGlCodeGuid, searchWord)
            )
          );
        } 
        // Else item create
        else {
          return firstCondition$.pipe(
            switchMap(guid => guid ? of(guid) : secondCondition$),
            switchMap(guid => guid ? of(guid) : thirdCondition$),
            tap(guid => {
              if (guid) {
                this.guid_glcode.patchValue(guid);
              }
            }),
            // Continue to getGLCodes after patching to update listings
            switchMap(guid => combineLatest([
              this.coaGuid$.pipe(startWith(null)),
              this.guid_glcode.valueChanges.pipe(startWith(guid)),
              this.serverSideFiltering.valueChanges.pipe(startWith(''))
            ]).pipe(
              debounceTime(300),  // Apply debounce here to limit rapid server requests
              switchMap(([coaGuid, savedGlCodeGuid, searchWord]) =>
                this.getGLCodes(coaGuid, savedGlCodeGuid, searchWord)
              )
            )),
            catchError(error => {
              console.error('Error in processing conditions', error);
              return of(null); // Continue with null if error occurs
            })
          );
        }
      })
    ).subscribe(glCodes => {
      if (glCodes) {
        this.glCodeList = glCodes;
        this.filteredOptions$.next([...this.glCodeList]);
      } // else "None" as the only option
    });
  }

  private getGLCodes(coaGuid: string, selectedGuid: string = '', searchWord: string = '') {
    const pagination = new Pagination();
    // Filter using branch's company's coaGuid
    pagination.limit = 20;
    pagination.conditionalCriteria = [
      { columnName: "chart_of_acc_guid", operator: "=", value: coaGuid },
      { columnName: "search_word", operator: "=", value: searchWord },
    ];
    pagination.sortCriteria = [
      { columnName: 'orderBy', value: 'name' },
      { columnName: 'order', value: 'ASC' },
    ];

  return this.glCodeService.getByCriteria(pagination, this.apiVisa).pipe(
    map(response => {
      const glCodes = response.data;

      // Check if the selected GL code is in the list, if not fetch it
      if (selectedGuid && !glCodes.some(gl => gl.bl_fi_mst_glcode.guid === selectedGuid)) {
        this.fetchGLCodeByGuid(selectedGuid).subscribe(guid_glcode => {
          glCodes.push(guid_glcode);
          this.filteredOptions$.next(glCodes);
        });
      }

      return glCodes;
    })
  );
  }

  onSelect(guid: string) {
    let selectedGLCode = this.glCodeList.find(item => item.bl_fi_mst_glcode.guid === guid);
    if (selectedGLCode) {
      this.guid_glcode.patchValue(guid);
    }
  }

  private fetchGLCodeByGuid(guid: string) {
    return this.glCodeService.getByGuid(guid, this.apiVisa).pipe(
      map(response => response.data)
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}

